// BeneAI drawer state — BeneAI-01.
//
// Provider pair that holds the drawer's open/closed state + the
// active conversation across route changes. Mounted once near the
// top of the authenticated app shell (``DashboardLayout``) so any
// page can open / close the drawer without prop drilling. The
// conversation cache survives navigation; closing the drawer
// preserves the message list so reopen feels instant.

import {
  createContext, useCallback, useContext, useMemo, useState,
} from "react";

import { getConversation } from "./api";
import type {
  BeneAIConfirmationStatus,
  BeneAIMessage,
  BeneAIPendingConfirmation,
} from "./types";


/**
 * Per-confirmation client state. ``status`` mirrors the server-side
 * lifecycle; ``inFlight`` flips true while a Confirm / Cancel network
 * call is round-tripping so the card disables its buttons without
 * waiting for the resolved status to land.
 */
export interface BeneAIConfirmationState {
  pending: BeneAIPendingConfirmation;
  status: BeneAIConfirmationStatus;
  inFlight: boolean;
}


interface BeneAIDrawerState {
  open: boolean;
  conversationId: string | null;
  messages: BeneAIMessage[];
  confirmations: Record<string, BeneAIConfirmationState>;
}


interface BeneAIDrawerActions {
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  appendMessage: (msg: BeneAIMessage) => void;
  setConversationId: (id: string | null) => void;
  replaceMessages: (msgs: BeneAIMessage[]) => void;
  /** Switch the drawer to a different conversation: clears local
   *  state and loads the messages for the new conversation. */
  loadConversation: (conversationId: string) => Promise<void>;
  registerPendingConfirmations: (
    rows: BeneAIPendingConfirmation[],
  ) => void;
  setConfirmationInFlight: (
    confirmationId: string, inFlight: boolean,
  ) => void;
  setConfirmationStatus: (
    confirmationId: string, status: BeneAIConfirmationStatus,
  ) => void;
  reset: () => void;
}


type BeneAIDrawerValue = BeneAIDrawerState & BeneAIDrawerActions;


const BeneAIDrawerContext = createContext<BeneAIDrawerValue | null>(null);


export function BeneAIDrawerProvider({
  children,
}: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationIdState] = useState<string | null>(
    null,
  );
  const [messages, setMessages] = useState<BeneAIMessage[]>([]);
  const [confirmations, setConfirmations] = useState<
    Record<string, BeneAIConfirmationState>
  >({});

  const openDrawer = useCallback(() => setOpen(true), []);
  const closeDrawer = useCallback(() => setOpen(false), []);
  const toggleDrawer = useCallback(() => setOpen((prev) => !prev), []);

  const appendMessage = useCallback((msg: BeneAIMessage) => {
    setMessages((prev) => {
      // Dedupe on ``message_id`` only. Optimistic user bubbles all
      // share ``sequence: -1`` and ``role: "user"`` — the earlier
      // ``(sequence + role)`` fallback meant every new optimistic
      // user message collided with the previous one and silently
      // overwrote it (the assistant reply still landed, so the UI
      // showed an answer with no question above it). Each
      // optimistic placeholder already carries a unique
      // ``pending-<ts>`` id; the canonical row that comes back from
      // the server has its own UUID, so ids never collide across
      // optimistic and canonical rows.
      const existingIdx = prev.findIndex(
        (m) => m.message_id === msg.message_id,
      );
      if (existingIdx >= 0) {
        const next = prev.slice();
        next[existingIdx] = msg;
        return next;
      }
      return [...prev, msg];
    });
  }, []);

  const setConversationId = useCallback((id: string | null) => {
    setConversationIdState(id);
  }, []);

  const replaceMessages = useCallback((msgs: BeneAIMessage[]) => {
    setMessages(msgs);
  }, []);

  const registerPendingConfirmations = useCallback(
    (rows: BeneAIPendingConfirmation[]) => {
      if (!rows || rows.length === 0) return;
      setConfirmations((prev) => {
        const next = { ...prev };
        for (const row of rows) {
          // Don't clobber inFlight or a more-advanced status if the
          // FE already moved on; the backend's PENDING is the baseline
          // only when no row exists yet.
          const existing = next[row.confirmation_id];
          if (existing && existing.status !== "pending") continue;
          next[row.confirmation_id] = {
            pending: row,
            status: row.status,
            inFlight: existing?.inFlight ?? false,
          };
        }
        return next;
      });
    },
    [],
  );

  const setConfirmationInFlight = useCallback(
    (confirmationId: string, inFlight: boolean) => {
      setConfirmations((prev) => {
        const row = prev[confirmationId];
        if (!row) return prev;
        return { ...prev, [confirmationId]: { ...row, inFlight } };
      });
    },
    [],
  );

  const setConfirmationStatus = useCallback(
    (confirmationId: string, status: BeneAIConfirmationStatus) => {
      setConfirmations((prev) => {
        const row = prev[confirmationId];
        if (!row) {
          return prev;
        }
        return {
          ...prev,
          [confirmationId]: {
            ...row, status, inFlight: false,
          },
        };
      });
    },
    [],
  );

  const reset = useCallback(() => {
    setConversationIdState(null);
    setMessages([]);
    setConfirmations({});
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    const detail = await getConversation(conversationId);
    setConversationIdState(detail.conversation_id);
    setMessages(detail.messages);
    setConfirmations({});
  }, []);

  const value = useMemo<BeneAIDrawerValue>(() => ({
    open,
    conversationId,
    messages,
    confirmations,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    appendMessage,
    setConversationId,
    replaceMessages,
    loadConversation,
    registerPendingConfirmations,
    setConfirmationInFlight,
    setConfirmationStatus,
    reset,
  }), [
    open, conversationId, messages, confirmations,
    openDrawer, closeDrawer, toggleDrawer,
    appendMessage, setConversationId, replaceMessages,
    loadConversation,
    registerPendingConfirmations, setConfirmationInFlight,
    setConfirmationStatus, reset,
  ]);

  return (
    <BeneAIDrawerContext.Provider value={value}>
      {children}
    </BeneAIDrawerContext.Provider>
  );
}


export function useBeneAIDrawer(): BeneAIDrawerValue {
  const ctx = useContext(BeneAIDrawerContext);
  if (ctx === null) {
    throw new Error(
      "useBeneAIDrawer must be used within BeneAIDrawerProvider",
    );
  }
  return ctx;
}
